import React, { useContext, useEffect, useState } from "react";
import "./Contact.scss";

// Components
import { IonButton, IonContent, IonInput, IonItem, IonList, IonLoading, IonNote, IonSelect, IonSelectOption, IonTextarea, SelectCustomEvent, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import BasicTemplate from "../../components/PageTemplates/BasicTemplate";

// Sendbird
import { GroupChannel } from "@sendbird/chat/groupChannel";
import { useQuery } from "@tanstack/react-query";
import { useSendbirdStateContext } from "@wearesweet/sendbird-uikit-react";
import sendbirdSelectors from "@wearesweet/sendbird-uikit-react/sendbirdSelectors";
import { PageHeader } from "../../components/PageHeader";
import { AuthenticatedUserContext } from "../../contexts/AuthenticatedUserContext";
import { ApiService } from "../../services";

interface ContactOption {
  id: number;
  member_id: number;
  name: string;
}

const ContactForm = (props: any) => {
  const [topic, setTopic] = useState<ContactOption | null>();
  const [subject, setSubject] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);
  const [isCreatingChannel, setIsCreatingChannel] = useState<boolean>(false);

  const { t } = useTranslation();
  const globalStore = useSendbirdStateContext();
  const createGroupChannel = sendbirdSelectors.getCreateGroupChannel(globalStore);
  const router = useIonRouter();
  const { member } = useContext(AuthenticatedUserContext);

  const { data, isLoading } = useQuery({
    queryKey: ["helpdesk-categories"],
    queryFn: () => {
      return ApiService.get("helpdesk-categories");
    },
  });

  const createNewTicket = () => {
    if (subject.length === 0 || !topic || message.length === 0) {
      const newErrors = [];

      if (subject.length === 0) {
        newErrors.push("subject");
      }

      if (!topic) {
        newErrors.push("topic");
      }

      if (message.length === 0) {
        newErrors.push("message");
      }

      setErrors(newErrors);
    } else {
      setIsCreatingChannel(true);

      const newChannelParams = {
        invitedUserIds: [member!.id.toString(), topic.member_id.toString()],
        operatorUserIds: [member!.id.toString(), topic.member_id.toString()],
        isDistinct: false,
        name: "\u2754 " + subject,
        customType: "h",
      };

      const redirect = (url: string) => {
        // give it some time for the message to go through
        setTimeout(() => {
          router.push(`/chat/channels/${url}/from-contact-us`);
        }, 1000);
      };

      createGroupChannel(newChannelParams)
        .then((value: GroupChannel) => {
          value
            .sendUserMessage({
              message,
            })
            .onSucceeded(() => {
              setIsCreatingChannel(false);
              redirect(value.url);
            })
            .onFailed(() => {
              setIsCreatingChannel(false);
              redirect(value.url);
            });
        })
        .catch((error: any) => {
          alert(error);
        });
    }
  };

  useEffect(() => {
    setTopic(data?.data?.filter((category: any) => category.id === parseInt(props.match.params.id, 10))[0]);
  }, [data?.data, props.match.params.id]);

  return (
    <BasicTemplate hideBackButton={false} hideHeader={false} title={t("helpdesk")}>
      <IonLoading isOpen={isCreatingChannel} />
      <IonContent className={"ion-padding"}>
        <PageHeader>{t("helpdesk")}</PageHeader>

        {isLoading && <IonLoading isOpen={true} />}
        {!isLoading && (
          <>
            {topic ? <p>{t("contactUsSelectedCategoryText").replace("%CATEGORY%", topic.name)}</p> : ""}
            <div className="ion-text-left contact-us__content">
              <div className={`input__container input__container--type-select ${errors.length > 0 && "has-error"}`}>
                <IonList>
                  <IonItem className={errors.includes("topic") ? "ion-invalid" : ""} style={{ "--padding-start": 0 }}>
                    <IonSelect onIonChange={(e: SelectCustomEvent) => setTopic(e.detail.value)} placeholder={"Please select a topic*"} value={topic}>
                      {data?.data?.map((option: ContactOption, index: number) => (
                        <IonSelectOption key={`option-${index}`} value={option}>
                          {option.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                    {errors.includes("topic") && (
                      <IonNote slot={"error"} color={"danger"}>
                        Please select a topic
                      </IonNote>
                    )}
                  </IonItem>

                  <IonItem className={errors.includes("subject") ? "ion-invalid" : ""} style={{ "--padding-start": 0 }}>
                    <IonInput onIonChange={(e) => setSubject(e.detail.value!)} placeholder={t("subject") + "*" || ""} />
                    {errors.includes("subject") && (
                      <IonNote slot={"error"} color={"danger"}>
                        Please enter a subject
                      </IonNote>
                    )}
                  </IonItem>

                  <IonItem className={errors.includes("message") ? "ion-invalid" : ""} style={{ "--padding-start": 0 }}>
                    <IonTextarea autoGrow={true} onIonChange={(e) => setMessage(e.detail.value!)} placeholder={t("message") + "*" || ""} />
                    {errors.includes("message") && (
                      <IonNote slot={"error"} color={"danger"}>
                        Please enter a message
                      </IonNote>
                    )}
                  </IonItem>
                </IonList>
              </div>
              <IonButton disabled={!topic} expand={"block"} className={"ion-margin-top"} onClick={createNewTicket} color="primary">
                {t("sendToChat")}
              </IonButton>
            </div>
          </>
        )}
      </IonContent>
    </BasicTemplate>
  );
};

export default ContactForm;
